import { useQuery, type UseQueryOptions } from '@tanstack/react-query';

import { ENDPOINT } from '~/constants/endpoints';

import { type KyRequestOptions } from '@vestigas/data';

import { vestigasApi } from '~/services/kyClient';

import { queryKeysDeliveryNote } from './queryKeys';
import { type DeliveryNoteSearchParams } from './types';
import { zodDeliveryNotesPayloadSchema } from './useQueryDeliveryNotes';

type DeliveryNoteSearchCountResponse = {
  totalAssets: number;
};

/**
 * Fetches the count of delivery notes based on search parameters from the API.
 *
 * @param searchParams - The search parameters for filtering delivery notes
 * @returns The total count of matching delivery notes
 * @see https://app.dev.vestigas.com/redoc#tag/Delivery-Note/operation/search_delivery_note_count_asset_delivery_note_search_count_post
 */
export const fetchDeliveryNoteSearchCount = async (
  searchParams: DeliveryNoteSearchParams,
  options?: KyRequestOptions,
) => {
  const response = await vestigasApi
    .post(ENDPOINT.DELIVERY_NOTE.GET_SEARCH_COUNT(), {
      json: searchParams,
      validate: {
        body: zodDeliveryNotesPayloadSchema,
      },
      ...options,
    })
    .json<DeliveryNoteSearchCountResponse>();

  return response.totalAssets;
};

/**
 * React Query based custom hook for getting the count of delivery notes matching the search criteria.
 *
 * @param searchParams - The search parameters for filtering delivery notes
 * @param options - Additional options for the useQuery hook
 * @returns The result of the useQuery hook containing the count
 */
export const useQueryDeliveryNoteSearchCount = (
  searchParams: DeliveryNoteSearchParams,
  options?: Omit<UseQueryOptions<number>, 'queryKey' | 'queryFn'>,
) => {
  return useQuery({
    queryFn: async ({ signal }: { signal: AbortSignal }) =>
      fetchDeliveryNoteSearchCount(searchParams, { signal }),
    queryKey: queryKeysDeliveryNote.getSearchCount(searchParams),
    ...options,
  });
};
